/**
 * AuthMorningBriefPreview — the editorial specimen that sits in the
 * right column of the login and register pages. Same design language
 * as the Home OS, in miniature. Communicates the product at first
 * sight, without a marketing wash.
 */
import { useLightPhase, HouseholdPulse, ModuleMark, Rule } from "./editorial";

export default function AuthMorningBriefPreview({ testid = "auth-brief-preview" }) {
  const { phase, hourLabel } = useLightPhase();
  const today = new Date().toLocaleDateString('en-NZ', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  return (
    <div data-testid={testid} className="max-w-[520px]">
      <p className="caption-ara">Ngakuru Home · this morning</p>

      <p
        className="mt-6 font-display italic text-[16px]"
        style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
      >
        Good morning, Erana.
      </p>

      <div className="mt-5">
        <HouseholdPulse
          items={[
            { label: 'Marama · home',      tone: 'family',   live: true  },
            { label: 'Kaia · at school',   tone: 'health',   live: false },
            { label: 'Kitchen · quiet',    tone: 'meals',    live: false },
            { label: '21° inside',         tone: 'home',     live: true  },
          ]}
        />
      </div>

      <h2
        className="mt-10 font-display leading-[1.05] text-[40px] sm:text-[48px] tracking-[-0.01em]"
        style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
      >
        The house is calm. Three matters ask for you today.
      </h2>

      <p className="mt-8 prose-ara max-w-[46ch]">
        Kaia has a dentist appointment before lunch, the rent is scheduled for
        Tuesday, and the kitchen is quietly running low. Everything else is in hand.
      </p>

      <ol className="mt-10">
        <li
          className="grid grid-cols-[auto_1fr] gap-x-6 py-5 border-t border-b"
          style={{ borderColor: 'var(--ara-line-soft)' }}
        >
          <span className="text-[13px] tabular-nums" style={{ color: 'var(--ara-ink-soft)' }}>01</span>
          <div>
            <div className="flex items-baseline gap-3">
              <span className="text-[16px] tabular-nums">10:00</span>
              <ModuleMark module="health" label="Health" />
            </div>
            <p
              className="mt-1.5 text-[19px] leading-snug"
              style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
            >
              Kaia · dentist, Ponsonby
            </p>
          </div>
        </li>
        <li
          className="grid grid-cols-[auto_1fr] gap-x-6 py-5 border-b"
          style={{ borderColor: 'var(--ara-line-soft)' }}
        >
          <span className="text-[13px] tabular-nums" style={{ color: 'var(--ara-ink-soft)' }}>02</span>
          <div>
            <div className="flex items-baseline gap-3">
              <span className="text-[16px] tabular-nums">Tue</span>
              <ModuleMark module="money" label="Money" />
            </div>
            <p
              className="mt-1.5 text-[19px] leading-snug"
              style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
            >
              Rent · $412 · Property Trust
            </p>
          </div>
        </li>
        <li
          className="grid grid-cols-[auto_1fr] gap-x-6 py-5 border-b"
          style={{ borderColor: 'var(--ara-line-soft)' }}
        >
          <span className="text-[13px] tabular-nums" style={{ color: 'var(--ara-ink-soft)' }}>03</span>
          <div>
            <ModuleMark module="meals" label="Meals" />
            <p
              className="mt-1.5 text-[19px] leading-snug"
              style={{ color: 'var(--ara-ink)', fontWeight: 400 }}
            >
              Kitchen provisions running low
            </p>
          </div>
        </li>
      </ol>

      <p
        className="mt-10 font-display italic text-[15px] max-w-[42ch]"
        style={{ color: 'var(--ara-ink-muted)', fontWeight: 400 }}
      >
        — The house is otherwise calm.
      </p>

      <div className="mt-16">
        <Rule />
        <p className="mt-6 caption-ara">
          {today} · Auckland · 17° · {phase} · composed at {hourLabel}
        </p>
      </div>
    </div>
  );
}
